import { Link } from 'react-router-dom'
import { LaunchBanner } from '../components/LaunchBanner'
import { TALYNTH_LIVE, TALYNTH_URL } from '../config/site'

function Products() {
  return (
    <>
      <LaunchBanner />
      <section className="hero hero--compact">
        <div className="container">
          <span className="product-card__eyebrow" style={{ background: 'var(--gradient-accent)' }}>
            Flagship product
          </span>
          <h1>Talynth</h1>
          <p className="tagline">
            POPIA compliance in 15 minutes, not 15 weeks — a self-service compliance platform for
            South African SMEs. No consultants, no spreadsheets, no guesswork.
          </p>
          <div className="hero__actions">
            {TALYNTH_LIVE ? (
              <>
                <a className="btn btn--primary" href={TALYNTH_URL}>
                  Visit talynth.tech
                </a>
                <a className="btn btn--ghost" href={TALYNTH_URL}>
                  See pricing
                </a>
              </>
            ) : (
              <button className="btn btn--primary is-disabled" type="button" disabled aria-disabled="true">
                Launching next week
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2>What Talynth does</h2>
          <p className="lead">
            A guided assessment produces an instant compliance score, auto-generates the
            legally-required documents, and runs your ongoing obligations year-round.
          </p>
          <ul className="feature-list">
            <li>Guided assessment mapped to POPIA&apos;s 8 conditions</li>
            <li>Instant, risk-weighted compliance score with gap analysis</li>
            <li>Auto-generated privacy policy, PAIA manual, and data register</li>
            <li>DSAR management with 30-day deadline tracking</li>
            <li>Breach response workflow with a 72-hour countdown</li>
            <li>Quarterly reviews, evidence management, and a full audit trail</li>
            <li>AI evidence verification and document drafting</li>
          </ul>
        </div>
      </section>

      <section className="section section--alt">
        <div className="container">
          <h2>Inside the platform</h2>
          <p className="lead">A look at Talynth in action.</p>
          <div className="shots">
            <figure className="shot">
              <img
                src="/images/talynth/dashboard.png"
                alt="Talynth compliance dashboard"
                loading="lazy"
              />
              <figcaption>
                Compliance dashboard — risk-weighted score, gaps, and quick stats at a glance.
              </figcaption>
            </figure>
            <figure className="shot">
              <img
                src="/images/talynth/assessment-wizard.png"
                alt="Talynth guided assessment wizard"
                loading="lazy"
              />
              <figcaption>
                Guided assessment — plain-language questions, no legal jargon.
              </figcaption>
            </figure>
            <figure className="shot">
              <img
                src="/images/talynth/monitoring-trend.png"
                alt="Talynth compliance monitoring over time"
                loading="lazy"
              />
              <figcaption>
                Ongoing monitoring — regulatory alerts and your compliance trend over time.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2>Common questions</h2>
          <div className="product-faq">
            <div className="product-faq__item">
              <h3>Why use Talynth?</h3>
              <p>
                Most South African SMEs know they need to comply with POPIA but have no practical
                way to do it: a compliance consultant typically costs tens of thousands of rand, and
                DIY checklists leave you unsure whether you&apos;re actually compliant. Talynth
                replaces that uncertainty with a guided assessment, a clear score, and the documents
                and workflows you need to act on it — for a subscription that starts free and scales
                with your business.
              </p>
            </div>

            <div className="product-faq__item">
              <h3>Is it compulsory for POPIA?</h3>
              <p>
                POPIA compliance itself is a legal requirement, not optional — the Act has been
                fully enforceable since 1 July 2021 and applies to any organisation that processes
                personal information in South Africa. The Information Regulator can issue
                administrative fines of up to R10 million and pursue criminal penalties of up to 10
                years for serious offences, and enforcement activity is rising sharply: over 2,300
                data-breach notifications were logged in the 2024/25 financial year alone. Talynth
                is not itself a legal requirement — you can comply without it — but it is the
                fastest and most affordable way to get there and to prove it if the Regulator, a
                client, or an insurer ever asks.
              </p>
            </div>

            <div className="product-faq__item">
              <h3>Does it save me time?</h3>
              <p>
                Yes. The guided assessment takes minutes, not the weeks a consultant engagement
                typically requires, and immediately produces a compliance score with your specific
                gaps. From there, Talynth auto-generates the documents POPIA requires (privacy
                policy, PAIA manual, data register, and more), tracks data subject access requests
                against their 30-day deadline, and walks you through the 72-hour breach notification
                window if you ever need it — so you&apos;re not starting from a blank page under
                time pressure.
              </p>
            </div>

            <div className="product-faq__item">
              <h3>What if I ignore it?</h3>
              <p>
                Only a small fraction of South African businesses currently meet a strong POPIA
                readiness bar, but enforcement is no longer theoretical: the Information Regulator
                has already issued multi-million-rand fines and is taking non-compliant
                organisations to court. The risk compounds the longer it&apos;s left unaddressed,
                while the cost of starting with Talynth is deliberately low — including a free tier
                to see where you stand today.
              </p>
            </div>
          </div>

          <p className="cta-row">
            {TALYNTH_LIVE ? (
              <a className="btn btn--primary" href={TALYNTH_URL}>
                Visit talynth.tech
              </a>
            ) : (
              <Link className="btn btn--primary" to="/contact">
                Register interest — launching next week
              </Link>
            )}
          </p>
        </div>
      </section>
    </>
  )
}

export default Products
